import { createBrowserRouter, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { lazy } from "react";
import { LandingPage } from "@/pages/LandingPage";
import { SpaceHomePage } from "@/pages/SpaceHomePage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { SpaceProvider, RequireSpaceAccess } from "@/features/spaces/SpaceProvider";

// Feature + auth pages are code-split: each loads only when its route is
// visited, and the chunk is immutable-cached by CloudFront after first load.
// (Landing, SpaceHome and NotFound stay eager — small and needed immediately.)
const HealthPage = lazy(() => import("@/pages/HealthPage").then((m) => ({ default: m.HealthPage })));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage").then((m) => ({ default: m.RegisterPage })));
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const ProfilePage = lazy(() => import("@/pages/auth/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const ToolsPage = lazy(() => import("@/pages/ToolsPage").then((m) => ({ default: m.ToolsPage })));
const ToolDetailPage = lazy(() => import("@/pages/ToolDetailPage").then((m) => ({ default: m.ToolDetailPage })));
const PetCarePage = lazy(() => import("@/pages/PetCarePage").then((m) => ({ default: m.PetCarePage })));
const ReadingPage = lazy(() => import("@/pages/ReadingPage").then((m) => ({ default: m.ReadingPage })));
const TravelPage = lazy(() => import("@/pages/TravelPage").then((m) => ({ default: m.TravelPage })));
const CookingPage = lazy(() => import("@/pages/CookingPage").then((m) => ({ default: m.CookingPage })));
const DiaryPage = lazy(() => import("@/pages/DiaryPage").then((m) => ({ default: m.DiaryPage })));
const GuidesPage = lazy(() => import("@/pages/GuidesPage").then((m) => ({ default: m.GuidesPage })));
const GuideCountryPage = lazy(() => import("@/pages/GuideCountryPage").then((m) => ({ default: m.GuideCountryPage })));
const GuideReaderPage = lazy(() => import("@/pages/GuideReaderPage").then((m) => ({ default: m.GuideReaderPage })));

/**
 * Application router (multi-tenant).
 *
 *   /                       → LandingPage (minimal: login / request access)
 *   /login|register|…       → auth pages (top-level)
 *   /profile, /health       → account + admin tools (top-level)
 *   /tools                  → public tool directory (no space, no login, no API)
 *   /tools/:toolKey         → one tool's own page, with a back link
 *   /:slug                  → SpaceProvider → SpaceHomePage (that space's dashboard)
 *   /:slug/<section>        → the feature page, gated by RequireSpaceAccess
 *   /:slug/guides/:cc/:guide → Travel Guides: countries → country → one guide
 *   /pets|/reading|…        → legacy redirects to the default space (`anmemory`)
 *   *                       → NotFoundPage
 *
 * Each section is wrapped in <RequireSpaceAccess section=…> so a private section
 * bounces anonymous viewers to /login, while a public one is viewable by anyone.
 */
const DEFAULT_SPACE = "anmemory";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <LandingPage /> },

      // Auth + account (top-level, no space).
      { path: "login", element: <LoginPage /> },
      { path: "register", element: <RegisterPage /> },
      { path: "verify-email", element: <VerifyEmailPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "health", element: <HealthPage /> },

      // Public tools — deliberately NOT space-scoped: they own no data, so there
      // is nothing for a space to gate and no slug for a visitor to know.
      // `/tools` is the card directory; each tool gets its own shareable URL.
      { path: "tools", element: <ToolsPage /> },
      { path: "tools/:toolKey", element: <ToolDetailPage /> },

      // Legacy single-tenant paths → default space (keeps old bookmarks working).
      { path: "pets", element: <Navigate to={`/${DEFAULT_SPACE}/pets`} replace /> },
      { path: "reading", element: <Navigate to={`/${DEFAULT_SPACE}/reading`} replace /> },
      { path: "travel", element: <Navigate to={`/${DEFAULT_SPACE}/travel`} replace /> },
      { path: "cooking", element: <Navigate to={`/${DEFAULT_SPACE}/cooking`} replace /> },
      { path: "diary", element: <Navigate to={`/${DEFAULT_SPACE}/diary`} replace /> },

      // A space and its sections.
      {
        path: ":slug",
        element: <SpaceProvider />,
        children: [
          { index: true, element: <SpaceHomePage /> },
          {
            path: "pets",
            element: (
              <RequireSpaceAccess section="pets">
                <PetCarePage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "reading",
            element: (
              <RequireSpaceAccess section="reading">
                <ReadingPage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "travel",
            element: (
              <RequireSpaceAccess section="travel">
                <TravelPage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "cooking",
            element: (
              <RequireSpaceAccess section="cooking">
                <CookingPage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "diary",
            element: (
              <RequireSpaceAccess section="diary">
                <DiaryPage />
              </RequireSpaceAccess>
            ),
          },
          // Travel Guides: countries → one country → one guide notebook.
          {
            path: "guides",
            element: (
              <RequireSpaceAccess section="guides">
                <GuidesPage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "guides/:country",
            element: (
              <RequireSpaceAccess section="guides">
                <GuideCountryPage />
              </RequireSpaceAccess>
            ),
          },
          {
            path: "guides/:country/:guide",
            element: (
              <RequireSpaceAccess section="guides">
                <GuideReaderPage />
              </RequireSpaceAccess>
            ),
          },
        ],
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
